/**
 * HouseholdVerificationStatusCard — right-column progress summary across
 * all household members. Status ONLY — never full findings (per spec).
 *
 * Bucketizes reports across every member_screening into three themes:
 *   • Employment Verification
 *   • Landlord Verification
 *   • Validation Review (criminal / sex_offender / id_verification / docs)
 *
 * Each row shows "X of Y complete" or "X pending" / "Complete" / "In Progress".
 */
import { CheckCircle2, Clock, AlertTriangle, Briefcase, Home, FileCheck2 } from "lucide-react";

const THEMES = [
  { key: "employment", label: "Employment Verification", types: ["employment_verification"], Icon: Briefcase },
  { key: "landlord",   label: "Landlord Verification",   types: ["landlord_verification", "eviction_search", "rental_history", "eviction"],   Icon: Home },
  { key: "validation", label: "Validation Review",       types: ["document_validation", "criminal_report", "ssn_verification", "criminal", "sex_offender", "id_verification"], Icon: FileCheck2 },
];

function summarize(reports) {
  const requested = reports.filter((r) => r.requested);
  if (requested.length === 0) return { state: "none", n: 0, total: 0 };
  const complete = requested.filter((r) => r.status === "complete").length;
  const inprog = requested.filter((r) => r.status === "in_progress").length;
  const flagged = requested.filter((r) => r.status === "flagged").length;
  if (flagged > 0) return { state: "flagged", n: flagged, total: requested.length };
  if (complete === requested.length) return { state: "complete", n: complete, total: requested.length };
  if (inprog > 0 || complete > 0) return { state: "in_progress", n: complete, total: requested.length };
  return { state: "pending", n: 0, total: requested.length };
}

const STATE_VIS = {
  complete:    { Icon: CheckCircle2, bg: "bg-emerald-50", ring: "ring-emerald-200", fg: "text-emerald-700", bar: "bg-emerald-500", label: "Complete" },
  in_progress: { Icon: Clock,         bg: "bg-blue-50",    ring: "ring-blue-200",    fg: "text-blue-700",    bar: "bg-blue-500",    label: "In Progress" },
  flagged:     { Icon: AlertTriangle, bg: "bg-rose-50",    ring: "ring-rose-200",    fg: "text-rose-700",    bar: "bg-rose-500",    label: "Needs Review" },
  pending:     { Icon: Clock,         bg: "bg-slate-50",   ring: "ring-slate-200",   fg: "text-slate-600",   bar: "bg-slate-400",   label: "Pending" },
  none:        { Icon: Clock,         bg: "bg-slate-50",   ring: "ring-slate-200",   fg: "text-slate-400",   bar: "bg-slate-300",   label: "Not Requested" },
};

export default function HouseholdVerificationStatusCard({ group, testid = "household-verification-status" }) {
  const members = group.member_screenings || [];
  // Flatten every member's reports into one list per theme.
  const allReports = members.flatMap((m) => m.reports || []);

  // Order-driven: only render a theme when at least one member has an
  // ordered/requested report in that theme. Per platform spec un-ordered
  // services must NOT show as "Not requested" — they must disappear.
  const rows = THEMES.map((t) => {
    const subset = allReports.filter((r) => t.types.includes(r.type) && r.requested);
    return { ...t, summary: summarize(subset), subset };
  }).filter((row) => row.subset.length > 0);

  if (rows.length === 0) {
    return (
      <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm p-6 text-center text-sm text-slate-500"
               data-testid={`${testid}-empty`}>
        No verification services ordered on this household.
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
      <header className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
          <CheckCircle2 className="h-4 w-4 text-slate-600" />
        </div>
        <div>
          <div className="ntn-eyebrow">Supporting Intelligence</div>
          <div className="ntn-h3 mt-0.5">Verification Status</div>
        </div>
      </header>

      <div className="divide-y divide-slate-100">
        {rows.map(({ key, label, summary, Icon }) => {
          const v = STATE_VIS[summary.state];
          const StateIcon = v.Icon;
          return (
            <div
              key={key}
              className="px-5 py-3 flex items-center gap-3"
              data-testid={`verification-theme-${key}`}
            >
              <div className={`h-8 w-8 rounded-md ${v.bg} ring-1 ${v.ring} flex items-center justify-center shrink-0`}>
                <Icon className={`h-4 w-4 ${v.fg}`} strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600">{label}</div>
                <div className="text-sm text-slate-900 mt-0.5 tabular-nums">
                  {summary.state === "complete" ? (
                    <span>{summary.total} of {summary.total} complete</span>
                  ) : summary.state === "flagged" ? (
                    <span>{summary.n} need{summary.n === 1 ? "s" : ""} review</span>
                  ) : summary.state === "in_progress" ? (
                    <span>{summary.n} of {summary.total} complete</span>
                  ) : (
                    <span>{summary.total} pending</span>
                  )}
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ring-1 ring-inset ${v.bg} ${v.ring} ${v.fg}`}>
                <StateIcon className="h-2.5 w-2.5" /> {v.label}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
